<script setup>
// NOTICE: KAPTION-INTEGRATION
// pdf-preview.VIEWER.1-6
//
// Reading a PDF someone sent used to mean downloading it, opening it in another
// app and coming back. Active Storage already serves attachments from this same
// origin with `Content-Disposition: inline`, so the browser's own PDF viewer can
// render it in place — no library, no copy of the file, and the download is
// still one click away for anyone who wants to keep it.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadFile } from '@chatwoot/utils';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  url: { type: String, required: true },
  name: { type: String, default: '' },
  extension: { type: String, default: 'pdf' },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const isDownloading = ref(false);

const title = computed(
  () => props.name || t('KAPTION_FILE_PREVIEW.TITLE_FALLBACK')
);

// pdf-preview.VIEWER.7 — an attachment's URL is built from the server's
// FRONTEND_URL (chat.kaptionai.com), which is not always the host the page runs
// on: Rumo Desktop opens the app at app.rumohq.com. Both names serve the same
// Rails app and Active Storage answers the same signed path on either, but
// chat.kaptionai.com carries X-Frame-Options: SAMEORIGIN on every response, so
// a frame opened from the other origin is refused and the viewer sits blank.
// Reading the document from our own origin keeps the frame same-origin wherever
// the app is opened — and keeps Download and Open in a new tab on that origin
// too. Only Active Storage paths are rewritten; a URL pointing straight at
// object storage is left exactly as the server gave it.
const ACTIVE_STORAGE_PATH = /^\/rails\/active_storage\//;

const documentUrl = computed(() => {
  try {
    const url = new URL(props.url, window.location.href);
    if (
      url.origin !== window.location.origin &&
      ACTIVE_STORAGE_PATH.test(url.pathname)
    ) {
      return `${window.location.origin}${url.pathname}${url.search}`;
    }
    return url.href;
  } catch (error) {
    return props.url;
  }
});

// `#view=FitH` is a PDF open parameter every built-in viewer understands: it
// fits the page to the width people actually read at.
const frameSrc = computed(() => `${documentUrl.value}#view=FitH`);

// The viewer always sits on a dark scrim, in either theme, so its controls are
// white on translucent white rather than theme-coloured.
const controlClass =
  'flex items-center h-8 gap-1.5 px-3 text-sm font-medium text-white transition-colors rounded-lg bg-white/15 hover:bg-white/25 disabled:opacity-50';

const close = () => emit('close');

const download = async () => {
  try {
    isDownloading.value = true;
    await downloadFile({
      url: documentUrl.value,
      type: 'file',
      extension: props.extension || 'pdf',
    });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const onKeydown = event => {
  if (event.key === 'Escape') close();
};

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      data-testid="kaption-pdf-preview"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      class="fixed inset-0 z-50 flex flex-col gap-3 p-4 bg-n-slate-12/70 backdrop-blur-sm"
      @click.self="close"
    >
      <header class="flex flex-wrap items-center flex-shrink-0 gap-3">
        <h1 class="min-w-0 m-0 text-base font-medium text-white truncate">
          {{ title }}
        </h1>
        <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
          <button
            type="button"
            data-testid="kaption-pdf-download"
            :disabled="isDownloading"
            :class="controlClass"
            @click="download"
          >
            <span class="i-lucide-download size-4" aria-hidden="true" />
            {{ t('KAPTION_FILE_PREVIEW.DOWNLOAD') }}
          </button>
          <a
            :href="documentUrl"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="kaption-pdf-new-tab"
            :class="controlClass"
          >
            <span class="i-lucide-external-link size-4" aria-hidden="true" />
            {{ t('KAPTION_FILE_PREVIEW.OPEN_IN_TAB') }}
          </a>
          <button
            type="button"
            data-testid="kaption-pdf-close"
            :aria-label="t('KAPTION_FILE_PREVIEW.CLOSE')"
            class="justify-center w-8 px-0"
            :class="controlClass"
            @click="close"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>
      </header>
      <iframe
        :src="frameSrc"
        :title="title"
        data-testid="kaption-pdf-frame"
        class="flex-grow w-full bg-n-solid-1 rounded-xl"
      />
      <p class="m-0 text-xs text-center text-white/70">
        {{ t('KAPTION_FILE_PREVIEW.UNSUPPORTED_HINT') }}
      </p>
    </div>
  </TeleportWithDirection>
</template>
